import { useEffect, useState } from 'react';
import { pushDataLayerForEvent } from '@lib/gtag';
import Tooltip from '@molecules/Tooltip';
import { Tooltip as NextUITooltip } from '@heroui/react';
import { colorPalette } from '@lib/constants';
import AvatarBadge from '@components/AvatarBadge';
import { getBarLayout, getInitials } from '@services/utils.service';
import useFonts from '@lib/hooks/useFonts';

const PollOption = ({
  optionNumber,
  name,
  weight,
  color = '#F97B5F',
  option,
  id,
  count,
  totalCount,
  currentRosFeature,
  pollOptions,
  pollColors,
  pollSettings,
  relativePercentage,
  fontFamily,
  fontSize,
  profileNames = [],
  otherCommenters = 0,
  showProfilePictures = true
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [roundedCorner, setRoundedCorner] = useState('rounded-[40px]');
  const [parentContainer, setParentContainer] = useState({ height: 'h-full', });
  const [optionNumContainer, setOptionNumContainer] = useState('w-[100px]');

  useEffect(() => {
    totalCount > 0 && pushDataLayerForEvent('populate_comments_power_poll');
  }, [totalCount > 0]);

  const getNoOfVotes = () => {
    if (count === 1) { return `${count} vote`; }
    return `${count} votes`;
  };

  const textColor = (currentRosFeature.type === 'pollWithOptions'
    ? pollColors?.pollWithOptionsLabelColor
    : pollColors?.pollLabelColor
  ) ?? 'white';

  useEffect(() => {
    if (!pollOptions) return;

    const {
      roundedCorner,
      optionNumContainer,
      parentContainer,
    } = getBarLayout(pollOptions.length);
    setRoundedCorner(roundedCorner);
    setOptionNumContainer(optionNumContainer);
    setParentContainer(parentContainer);
  }, [currentRosFeature.type, pollOptions]);

  const { getFontStyle } = useFonts();
  const fontStyle = getFontStyle(fontFamily?.fontFamily || 'Sans-Serif');

  return <>
    {
      currentRosFeature
      && <div className={`w-full flex items-center max-h-[200px] ${parentContainer.height} z-10`}>

        <div
          style={{ color: textColor }}
          className={`flex ${optionNumContainer} h-full items-center text-white justify-center rounded-[24px] mr-4`}>
          <span style={{ ...fontStyle, fontSize }} className={'test-base font-medium text-center'}>{optionNumber}</span>
        </div>

        <div className={`w-full h-full flex ${roundedCorner} overflow-hidden`}>
          <div id={name} className={'w-full h-full flex items-center relative'}>

            {/* Filled section */}
            <div
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              style={{ width: `${relativePercentage}%`, background: color }}
              className={'h-full cursor-pointer absolute left-0 top-0 z-0 overflow-hidden ease-in-out duration-300'}
            ></div>

            {/* Lightened section */}
            {<div
              style={{
                width: '100%',
                left: 0,
                background: color,
                opacity: 0.5
              }}
              className={'h-full absolute top-0 z-0'}
            ></div>}

            <div className={'w-full h-full flex items-center relative z-10'}>

              <div id={`option-${id}`} style={{ ...fontStyle, fontSize }} className={'flex items-center gap-4 font-normal break-all whitespace-normal px-10'} >
                <p style={{ color: `${currentRosFeature.type === 'pollWithOptions' ? pollColors?.pollWithOptionsLabelColor : pollColors?.pollLabelColor}` }} >{name}</p>
              </div>

              <div className={'break-normal ml-auto flex items-center pr-10'} style={{ fontSize }}>
                {/* Profile Icons with Right Overlapping Effect */}

                {showProfilePictures && (
                  <div className="flex items-center relative">
                    {profileNames.slice(0, 4).map((username, index) => {
                      return (
                        <NextUITooltip
                          key={index}
                          content={
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
                                style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
                              >
                                {getInitials(username)}
                              </div>
                              <span className="text-sm font-normal text-[#212121]">{username}</span>
                            </div>
                          }

                          placement="top"
                          showArrow
                          classNames={{
                            base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
                            content: ['py-2 px-4 shadow-xl', 'text-black bg-white'],
                          }}
                        >
                          <div className={'flex flex-row-reverse'}>
                            <AvatarBadge name={getInitials(username)} index={index} />
                          </div>
                        </NextUITooltip>
                      );
                    })}

                    {otherCommenters > 0 && (
                      <NextUITooltip
                        placement="top"
                        showArrow
                        classNames={{
                          base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
                          content: ['py-2 px-4 shadow-xl', 'text-black bg-white max-h-48 overflow-y-auto rounded-md', 'z-9999999'],
                        }}
                        content={
                          <div className="flex flex-col gap-2">
                            {profileNames
                              .slice(4)
                              .sort((a, b) => a.localeCompare(b))
                              .map((username, idx) => {
                                const initials = getInitials(username);
                                const bgColor = colorPalette[(4 + idx) % colorPalette.length];

                                return (
                                  <div key={idx} className="flex items-center gap-2">
                                    <div
                                      className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
                                      style={{ backgroundColor: bgColor }}
                                    >
                                      {initials}
                                    </div>
                                    <span className="text-sm font-normal text-[#212121]">{username}</span>
                                  </div>
                                );
                              })}
                          </div>
                        }
                      >
                        <div
                          className="w-[38px] h-[38px] rounded-full flex justify-center items-center relative z-4"
                          style={{
                            backgroundColor: '#FFFFFF',
                            marginLeft: '0px',
                            zIndex: 9999,
                          }}
                        >
                          <div className="text-[#212121] text-sm font-semibold font-['Inter'] leading-tight">
                            {otherCommenters}+
                          </div>
                        </div>
                      </NextUITooltip>
                    )}

                  </div>
                )}

                {/* Relative Percentage */}
                <div
                  className={`${fontSize} break-normal pl-4`}
                  style={{ color: textColor }}
                >
                  {pollSettings?.DisplayStyle === 'percentage' ? `${weight}%` : getNoOfVotes()}
                </div>
              </div>

            </div>

            <div className='relative'>
              {
                showTooltip && <Tooltip
                  type={((option.count !== 0 && currentRosFeature?.type === 'freeForm') || (option.weight !== 0 && currentRosFeature?.type === 'pollWithOptions')) ? 'poll' : 'zeroPercentage'}
                  directions={'bottom'}
                  arrowDirections={'top'}
                  className2='p-2 font-medium text-xs leading-2.5 rounded-lg text-white shadow-comment-overlay bg-ToolTip backdrop-filter w-max'
                  className1='font-medium text-xs leading-2.5 border-gray-50 w-max'>  {`${option.count !== undefined ? option.count : 0} vote(s) out of ${totalCount || 0}`}</Tooltip>
              }
            </div>
          </div>
        </div>
      </div >
    }
  </>;
};

export default PollOption;
